import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminFinesApi } from "../../http/fines/api";
import { Fine } from "../../http/fines/types";
import { formatTimeStamp } from "../../types/utils";
import Button from "../ui/Button";
import Table from "../ui/Table";
import NotifyFineDialog from "./NotifyFineDialog";

type FineNotificationsProps = {
  fine?: Fine | null;
  onUpdate: () => void;
};

const FineNotifications: FC<FineNotificationsProps> = ({ fine, onUpdate }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const finesApi = useAdminFinesApi();
  const filters = useListFilters({ isSearchable: false });
  const aborter = useRef(new AbortController());
  const { id: fineId } = useParams<{ id: string }>();
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);

  const result = finesApi.useFineNotifications(filters.state, aborter.current.signal, fineId!);
  const [data] = useDataInitializer(result.data, result.error);

  return (
    <>
      <Table
        filters={filters}
        buttons={{
          main: !!userSettings.permissions.fines.update && (
            <Button
              buttonProps={{
                onClick: () => setShowNotifyDialog(true),
                disabled: !fine || fine.status === "archived",
              }}
            >
              {i18n.t.fines.notifyDriver}
            </Button>
          ),
        }}
        head={
          <>
            <th align="left">{i18n.t.fines.sentBy}</th>
            <th align="left">{i18n.t.fines.sentAt}</th>
          </>
        }
        renderRow={(item) => (
          <>
            <td>{item.sentBy.fullName}</td>
            <td>{formatTimeStamp(item.sentAt, i18n.language)}</td>
          </>
        )}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {showNotifyDialog && !!fine && (
        <NotifyFineDialog
          fine={fine}
          onClose={() => setShowNotifyDialog(false)}
          onSent={() => {
            setShowNotifyDialog(false);
            onUpdate();
          }}
        />
      )}
    </>
  );
};

export default FineNotifications;
